import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiUser, FiMail, FiLock, FiEye, FiEyeOff, FiAtSign } from 'react-icons/fi';
import { authAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Register() {
  const [form, setForm]       = useState({ full_name: '', username: '', email: '', password: '' });
  const [show, setShow]       = useState(false);
  const [loading, setLoading] = useState(false);
  const { login }             = useAuth();
  const navigate              = useNavigate();

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    const { full_name, username, email, password } = form;
    if (!full_name || !username || !email || !password) return toast.error('All fields are required.');
    if (password.length < 6) return toast.error('Password must be at least 6 characters.');
    if (!/^[a-zA-Z0-9._]+$/.test(username)) return toast.error('Username: only letters, numbers, dots, underscores.');
    setLoading(true);
    try {
      const res = await authAPI.register(form);
      login(res.data.token, res.data.user);
      toast.success('Welcome to MSocial 🎉');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed.');
    } finally { setLoading(false); }
  };

  const strength = (() => {
    const p = form.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 6)  s++;
    if (p.length >= 10) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^a-zA-Z0-9]/.test(p)) s++;
    return s;
  })();
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'][strength];
  const strengthColor = ['', '#EF4444', '#F59E0B', '#10B981', '#059669', '#7B2FF7'][strength];

  return (
    <div className="auth-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 'clamp(300px,95vw,490px)', padding: 'clamp(12px,4vw,24px)' }}>
        <div className="auth-box card fade-in">
          <div className="auth-logo">
            <img src="https://noris.in/wp-content/uploads/2023/01/logo.png" alt="Noris"
              onError={e => { e.target.style.display = 'none'; }} />
            <h1 className="gradient-text" style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.3rem,4vw,1.9rem)' }}>
              Join MSocial
            </h1>
            <p style={{ color: 'var(--text-2)', fontSize: 'var(--fs-sm)', marginTop: 4 }}>Create your account and start sharing!</p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {[
              { name: 'full_name', label: 'Full Name', type: 'text', Icon: FiUser, placeholder: 'Your full name' },
              { name: 'username',  label: 'Username',  type: 'text', Icon: FiAtSign, placeholder: 'e.g. john_doe' },
              { name: 'email',     label: 'Email',     type: 'email', Icon: FiMail,  placeholder: 'you@example.com' },
            ].map(({ name, label, type, Icon, placeholder }) => (
              <div key={name} className="input-group">
                <label>{label}</label>
                <div style={{ position: 'relative' }}>
                  <Icon style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} size={15} />
                  <input className="input" style={{ paddingLeft: 42 }} type={type} name={name}
                    placeholder={placeholder} value={form[name]} onChange={handleChange}
                    autoComplete={name === 'username' ? 'username' : name === 'email' ? 'email' : 'name'} />
                </div>
                {name === 'username' && form.username && !/^[a-zA-Z0-9._]+$/.test(form.username) && (
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--danger)' }}>Only letters, numbers, . and _</span>
                )}
              </div>
            ))}

            {/* Password field */}
            <div className="input-group">
              <label>Password</label>
              <div style={{ position: 'relative' }}>
                <FiLock style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} size={15} />
                <input className="input" style={{ paddingLeft: 42, paddingRight: 44 }}
                  type={show ? 'text' : 'password'} name="password"
                  placeholder="At least 6 characters"
                  value={form.password} onChange={handleChange} autoComplete="new-password" />
                <button type="button" onClick={() => setShow(p => !p)}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4 }}>
                  {show ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                </button>
              </div>
              {form.password && (
                <div>
                  <div style={{ display: 'flex', gap: 3, marginTop: 5 }}>
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= strength ? strengthColor : 'var(--border)', transition: 'background .3s' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 'var(--fs-xs)', color: strengthColor, fontWeight: 600 }}>{strengthLabel}</span>
                </div>
              )}
            </div>

            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)', textAlign: 'center', lineHeight: 1.6 }}>
              By signing up you agree to our{' '}
              <span style={{ color: 'var(--primary)', cursor: 'pointer' }}>Terms</span> &amp;{' '}
              <span style={{ color: 'var(--primary)', cursor: 'pointer' }}>Privacy Policy</span>
            </p>

            <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading}>
              {loading
                ? <span className="spinner" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,.3)' }} />
                : 'Create Account'
              }
            </button>
          </form>

          <div className="auth-divider"><span>Already have an account?</span></div>
          <Link to="/login" className="btn btn-outline w-full" style={{ justifyContent: 'center' }}>Log In</Link>
        </div>

        <p style={{ textAlign: 'center', fontSize: 'var(--fs-xs)', color: 'var(--text-3)', marginTop: 'var(--sp-3)' }}>
          © 2024 MSocial by{' '}
          <a href="https://noris.in" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Noris Solutions</a>
        </p>
      </div>
    </div>
  );
}
